/**
 * Best-effort registrable domain.
 * `shopee.co.id` and `lazada.co.id` must stay distinct — do not collapse to `co.id`.
 */
export function normalizeHostname(hostname: string): string {
  return hostname.toLowerCase().replace(/^www\./, '');
}

export function toRegistrableDomain(hostname: string): string {
  const host = normalizeHostname((hostname || '').replace(/^\.+|\.+$/g, ''));
  const parts = host.split('.').filter(Boolean);
  if (parts.length <= 2) return host;

  const last = parts[parts.length - 1];
  const second = parts[parts.length - 2];
  const secondLevelPublicSuffixes = new Set(['co', 'com', 'org', 'net', 'gov', 'ac', 'edu']);

  if (last.length === 2 && secondLevelPublicSuffixes.has(second)) {
    return parts.slice(-3).join('.');
  }

  return `${second}.${last}`;
}

export function getBaseDomain(host: string): string {
  return toRegistrableDomain(host);
}

export function hostnamesMatch(hostname1: string, hostname2: string): boolean {
  return normalizeHostname(hostname1) === normalizeHostname(hostname2);
}

export function matchMarketplaceDefinitionForUrl<T extends { domain?: string }>(
  url: string | undefined,
  definitions: T[]
): T | null {
  if (!url) return null;
  try {
    const host = normalizeHostname(new URL(url).hostname);
    let best: T | null = null;
    let bestLen = -1;
    for (const def of definitions) {
      const domain = normalizeHostname(String(def.domain || ''));
      if (!domain) continue;
      const match = host === domain || host.endsWith(`.${domain}`);
      if (match && domain.length > bestLen) {
        best = def;
        bestLen = domain.length;
      }
    }
    return best;
  } catch {
    return null;
  }
}
